import React from "react";
import { Modal, Timeline, Tag, Empty, Typography, Space, Descriptions, Spin } from "antd";
import {
  HistoryOutlined,
  EditOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { normalizeHistoryEntry } from "./modificationHistoryUtils";
import "./ModificationHistoryModal.css";

const { Text } = Typography;

// ✅ Uniformiser l’affichage de dates (localisé par langue i18n)
const formatDate = (dateString, lang, t) => {
  if (!dateString) return t("modification.historyModal.date.unknown");
  const date = new Date(dateString);
  if (isNaN(date)) return t("modification.historyModal.date.unknown");

  return date.toLocaleString(lang || "en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ✅ Couleurs des tags selon le champ modifié
const getChangeColor = (field) => {
  switch (field) {
    case "valeur":
      return "blue";
    case "statut":
      return "orange";
    case "unite":
      return "purple";
    case "normale":
      return "cyan";
    case "nom":
      return "green";
    default:
      return "default";
  }
};

export default function ModificationHistoryModal({
  visible,
  onClose,
  document,
  history, // ✅ tableau [{ savedAt, modifications: [] }, ...]
  total, // optionnel
  loading,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";

  // ✅ Priorité à la prop history, fallback doc
  const historyDataRaw = Array.isArray(history)
    ? history
    : (document?.modification_history ||
      document?.historique_modifications ||
      document?.change_history ||
      []);

  // ✅ Normalisation
  const historyData = historyDataRaw
    .map(normalizeHistoryEntry)
    .filter(Boolean)
    .filter((e) => e.savedAt);

  // ✅ Grouping par savedAt (sessions)
  const groupedHistory = React.useMemo(() => {
    const groups = {};
    for (const entry of historyData) {
      const key = entry.savedAt;
      if (!groups[key]) {
        groups[key] = { savedAt: key, modifications: [], totalChanges: 0 };
      }
      groups[key].modifications.push(...(entry.modifications || []));
      groups[key].totalChanges += entry.totalChanges || entry.modifications?.length || 0;
    }
    return Object.values(groups).sort(
      (a, b) => new Date(b.savedAt) - new Date(a.savedAt)
    );
  }, [historyData]);

  // ✅ Labels champs via i18n (fallback: field)
  const getFieldLabel = (field) => {
    return (
      t(`modification.historyModal.timeline.fieldLabels.${field}`, field) || field
    );
  };

  return (
    <Modal
      title={
        <Space>
          <HistoryOutlined style={{ color: "#1890ff" }} />
          <span>{t("modification.historyModal.title")}</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      className="modification-history-modal"
    >
      {/* ✅ Infos du document */}
      <div
        style={{
          marginBottom: 24,
          padding: 16,
          background: "#f9fafb",
          borderRadius: 8,
        }}
      >
        <Descriptions size="small" column={2}>
          <Descriptions.Item label={t("modification.historyModal.docInfo.document")}>
            {document?.nom_fichier || t("modification.historyModal.docInfo.unknown")}
          </Descriptions.Item>

          <Descriptions.Item label={t("modification.historyModal.docInfo.type")}>
            <Tag color="blue">{document?.type_document || t("modification.historyModal.docInfo.na")}</Tag>
          </Descriptions.Item>

          <Descriptions.Item label={t("modification.historyModal.docInfo.doctor")}>
            {document?.medecin_nom || t("modification.historyModal.docInfo.unknownDoctor")}
          </Descriptions.Item>

          <Descriptions.Item label={t("modification.historyModal.docInfo.uploadDate")}>
            {formatDate(document?.date_upload || document?.createdAt, lang, t)}
          </Descriptions.Item>
        </Descriptions>
      </div>

      {/* ✅ Loading */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
            tip={t("modification.historyModal.loading")}
          />
        </div>
      ) : groupedHistory.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div>
              <Text type="secondary">{t("modification.historyModal.empty.title")}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("modification.historyModal.empty.subtitle")}
              </Text>
            </div>
          }
          style={{ padding: "60px 0" }}
        />
      ) : (
        <>
          {/* ✅ Timeline */}
          <Timeline
            mode="left"
            items={groupedHistory.map((group) => ({
              color: "blue",
              dot: <ClockCircleOutlined style={{ fontSize: 16 }} />,
              label: (
                <div style={{ width: 140, textAlign: "right" }}>
                  <Text strong>{formatDate(group.savedAt, lang, t)}</Text>
                </div>
              ),
              children: (
                <div className="history-group">
                  {/* ✅ Résumé */}
                  <div className="history-group-header">
                    <Tag color="blue" icon={<EditOutlined />}>
                      {t("modification.historyModal.timeline.changesTag", {
                        count: group.totalChanges,
                      })}
                    </Tag>
                  </div>

                  {/* ✅ Détails */}
                  <div className="history-modifications">
                    {group.modifications.map((mod, idx) => (
                      <div key={idx} className="history-modification-item">
                        <div className="modification-header">
                          <Space>
                            <Tag color={getChangeColor(mod.field)}>
                              {getFieldLabel(mod.field)}
                            </Tag>
                            <Text strong>{mod.parameter}</Text>
                          </Space>
                        </div>

                        {!mod.legacy && <div className="modification-values">
                          <div className="value-change">
                            <div className="old-value">
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {t("modification.historyModal.timeline.before")}
                              </Text>
                              <Text delete style={{ marginLeft: 8 }}>
                                {mod.oldValue ?? "-"}
                              </Text>
                            </div>

                            <div className="arrow">→</div>

                            <div className="new-value">
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {t("modification.historyModal.timeline.after")}
                              </Text>
                              <Text strong style={{ marginLeft: 8, color: "#1890ff" }}>
                                {mod.newValue ?? "-"}
                              </Text>
                            </div>
                          </div>
                        </div>}
                      </div>
                    ))}
                  </div>
                </div>
              ),
            }))}
          />

          {/* ✅ Stats */}
          <div
            style={{
              marginTop: 24,
              padding: 16,
              background: "#f0f5ff",
              borderRadius: 8,
              display: "flex",
              justifyContent: "space-around",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <Text type="secondary">{t("modification.historyModal.stats.totalSessions")}</Text>
              <div style={{ fontSize: 20, fontWeight: 600, color: "#1890ff" }}>
                {total ?? groupedHistory.length}
              </div>
            </div>

            <div style={{ textAlign: "center" }}>
              <Text type="secondary">{t("modification.historyModal.stats.totalChanges")}</Text>
              <div style={{ fontSize: 20, fontWeight: 600, color: "#52c41a" }}>
                {groupedHistory.reduce((sum, g) => sum + g.totalChanges, 0)}
              </div>
            </div>

            <div style={{ textAlign: "center" }}>
              <Text type="secondary">{t("modification.historyModal.stats.lastModified")}</Text>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#8c8c8c" }}>
                {formatDate(groupedHistory[0]?.savedAt, lang, t)}
              </div>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
